<script setup lang="ts">
    import { computed } from 'vue';
    import { formatNumber } from '@shared/utils/formatNumber';
    import InsightsIcon from './InsightsIcon.vue';
    import type { InsightsIssueSummary } from '../insights-dummy-data';
    import { INSIGHTS_COLORS, formatPercent } from '../insights-theme';

    const props = defineProps<{
        issues: InsightsIssueSummary[];
        averageOpenPercentage: number;
        // Show an Account column (subscription or multi-account scope)
        showAccount?: boolean;
    }>();

    const maxOpen = computed(() => Math.max(...props.issues.map((i) => i.Counts.OpenPercentage), 1));
    const maxClick = computed(() => Math.max(...props.issues.map((i) => i.Counts.ClickPercentage), 1));

    function formatDate(value: string): string {
        return new Date(value).toLocaleDateString(navigator.language, { day: 'numeric', month: 'short' });
    }
</script>

<template>
    <table class="table table-condensed table-hover">
        <thead>
            <tr>
                <th scope="col">Mailing</th>
                <th v-if="showAccount" scope="col">Account</th>
                <th scope="col" class="text-right p-lg-r">Sent</th>
                <th scope="col" class="text-nowrap">Open rate</th>
                <th scope="col" class="text-nowrap">Click rate</th>
            </tr>
        </thead>
        <tbody>
            <!-- Keyed by position so the bars animate between months instead of being recreated -->
            <tr v-for="(issue, index) in issues" :key="index">
                <td class="insights-mailing-cell">
                    <a :href="`/Issues/${issue.IssueId}/Statistics`" class="fw-600 d-block text-nowrap text-overflow-ellipsis" v-tooltip.top="issue.Name">{{ issue.Name }}</a>
                    <span class="text-sm d-block text-nowrap text-overflow-ellipsis" v-tooltip.top="issue.Subject">{{ issue.Subject }} ({{ formatDate(issue.Sent) }})</span>
                </td>
                <td v-if="showAccount">
                    <span class="insights-account-name d-block text-nowrap text-overflow-ellipsis" v-tooltip.top="issue.Account.Name">{{ issue.Account.Name }}</span>
                </td>
                <td class="text-right text-nowrap p-lg-r fw-600">{{ formatNumber(issue.Counts.SentCount) }}</td>
                <td class="fw-600">
                    <div class="d-flex flex-direction-col gap-xxs">
                        <span>
                            {{ formatPercent(issue.Counts.OpenPercentage) }}
                            <span
                                v-if="issue.Counts.OpenPercentage >= averageOpenPercentage * 1.15"
                                class="c-warning"
                                role="img"
                                v-tooltip.top="'Well above your average open rate'"
                                aria-label="Well above your average open rate">
                                <InsightsIcon icon="star" :size="14" />
                            </span>
                        </span>
                        <span class="insights-track insights-track-thin d-block round">
                            <span
                                class="insights-track-fill d-block"
                                :style="{ width: `${(issue.Counts.OpenPercentage / maxOpen) * 100}%`, background: INSIGHTS_COLORS.opened }"></span>
                        </span>
                    </div>
                </td>
                <td class="fw-600">
                    <div class="d-flex flex-direction-col gap-xxs">
                        {{ formatPercent(issue.Counts.ClickPercentage) }}
                        <span class="insights-track insights-track-thin d-block round">
                            <span
                                class="insights-track-fill d-block"
                                :style="{ width: `${(issue.Counts.ClickPercentage / maxClick) * 100}%`, background: INSIGHTS_COLORS.clicked }"></span>
                        </span>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</template>
